import _ from 'lodash';
import React, { Component, PropTypes } from 'react';
import { Modal } from 'react-bootstrap';
import { getWeatherCategoryFromCondition } from '../../utils/WeatherUtils';
import { formatWsiDate, formatDate } from '../../utils/DateUtils';
import Timestamp from '../Timestamp';
import ComponentTitle from '../shared/ComponentTitle';
import WeatherAlertIcon from './WeatherAlertIcon';
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';

/**
 *
 * Class representing the container for the multiday and hourly weather forecast components.
 *
 */
class WeatherAlerts extends Component {

  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    title: PropTypes.string,
    alertsPageUri: PropTypes.string,
    allowedAlerts: PropTypes.array,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    title: "Weather Advisory In Effect",
    alertsPageUri: '',
    allowedAlerts: []
  }

  state = {
    showModal: false,
    expandedAlertIndex: -1,
    showAllAlerts: false
  }

  _setModalVisible(alertIndex = -1){
    this.setState({
      showModal: true,
      expandedAlertIndex: alertIndex
    });
  }

  // Hide the modal and unassign any expanded alert
  _setModalHidden(){
    this.setState({
      showModal: false,
      expandedAlertIndex: -1
    });
  }

  _toggleAlertListItems(){
    this.setState({
      showAllAlerts: !this.state.showAllAlerts
    });
  }

  // Assign the alert which will display its full text in the modal
  _setExpandedIndex(alertIndex = -1){
    const { expandedAlertIndex } = this.state;
    const newExpandedIndex = alertIndex !== expandedAlertIndex ? alertIndex : -1;

    this.setState({
      expandedAlertIndex: newExpandedIndex
    });
  }

  // When a user clicks on an alert show them either the station's default page, or display
  // a modal with the full alert in it.
  _showFullAlerts(alertIndex = -1, event){
    const { config, location } = this.context;
    const { alertsPageUri } = this.props;
    const { affiliate: { locations: stationLocations = [] } = {} } = config;

    // If the current location isn't a default location in the config, then show the modal with the alert in it,
    // otherwise the default browser behaviour will follow the link given in the prop alertsPageUri
    if (!_.find(stationLocations, { 'zipcode': Number(location.zipcode)}) || !alertsPageUri){
      event.preventDefault();
      this._setModalVisible(alertIndex);
    }
  }

  // Remove any alerts that are not in the filter type, and sort them into the appropriate order. The order is specified
  // by whatever order the allowedAlerts comes in as, and then by date within that list.
  _filterAlertsByType(alerts, allowedAlerts){
    const filteredAlerts = _.filter(alerts,
      (alert) => (_.find(allowedAlerts,
        (filterItem) => this._compareAlertFilter(filterItem, alert)
        )
      )
    );

    const alertWithBackgroundColor = _.map(filteredAlerts,
      (alert) => {
        const alertFilter = _.find(allowedAlerts,
          (filterItem) => this._compareAlertFilter(filterItem, alert)
        );

        return _.assign(alert, {backgroundColor: alertFilter.backgroundColor});
      }
    );

    return _.sortBy(alertWithBackgroundColor,
      (alert) => (_.findIndex(allowedAlerts,
          (filterItem) => this._compareAlertFilter(filterItem, alert)
        )
      )
    );
  }

  _compareAlertFilter(filterItem, alert) {
    const alertTitle = alert['headline'] || '';

    return filterItem.type.toLowerCase() === alertTitle.split(' ').pop().toLowerCase();
  }

  render() {
    const { showModal, expandedAlertIndex, showAllAlerts } = this.state;
    const {
      FRN_rawResponses: [
        {
          data: alerts = []
        } = {}
      ] = [],
      title,
      allowedAlerts = [],
      alertsPageUri,
      timestampOptions = {}
    } = this.props;

    let alertsToDisplay;
    if (allowedAlerts.length > 0) {
      alertsToDisplay = this._filterAlertsByType(alerts, allowedAlerts);
    } else {
      alertsToDisplay = alerts;
    }

    if (alertsToDisplay.length === 0) {
      return null;
    }

    const items = alertsToDisplay.map((alert, i) =>
      <div className="WeatherAlert-listItem" key={i}
           style={{backgroundColor: alert.backgroundColor || '#d20202'}}>
        <WeatherAlertItem
          alert={alert}
          onClick={this._showFullAlerts.bind(this, i)}/>
      </div>);

    return (
      <div className="WeatherAlert">
        <a className="WeatherAlert-stationAlertLink" href={alertsPageUri} target="_top">
          <div className="WeatherAlert-listAdvisory">
            {showAllAlerts ? items : items[0]}
          </div>
        </a>
        {alertsToDisplay.length > 1 ?
          <div className="WeatherAlert-totalPillContainer" onClick={() => this._toggleAlertListItems()}>
            <div className="WeatherAlert-totalPill" style={{color: alertsToDisplay[0].backgroundColor || '#d20202'}}>
              <span className="WeatherAlert-totalPillIcon">
                <WeatherAlertIcon iconType={'alert'} />
              </span>
              <span className="WeatherAlert-totalValue">{alertsToDisplay.length}</span>
            </div>
          </div>
          : null}
        <WeatherAlertModal title={title}
                           showModal={showModal}
                           alerts={alertsToDisplay}
                           expandedAlertIndex={expandedAlertIndex}
                           onHideModal={this._setModalHidden.bind(this)}
                           onAlertClick={this._setExpandedIndex.bind(this)}
                           timestampOptions={timestampOptions}/>
      </div>
    );
  }
}

/**
 * Class representing a weather alert item.
 */
class WeatherAlertItem extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    onClick: PropTypes.func
  }

  _getTimeDuration(startTime, endTime){
    let duration = '';

    if (startTime){
      duration = ' From ' + formatDate(formatWsiDate(startTime), 'dddd, MMMM Do Y, h:mm a z');
    }

    if (endTime){
      duration += ' until ' + formatDate(formatWsiDate(endTime), 'dddd, MMMM Do Y, h:mm a z');
    }

    return duration;
  }

  render() {
    const {
      alert: {
        headline: title,
        startTime,
        endTime
      } = {},
      onClick
    } = this.props;
    const category = getWeatherCategoryFromCondition(title);

    return (
      <div className="WeatherAlertItem" onClick={onClick} >
        <div className="WeatherAlertItem-advisoryImage">
          <WeatherAlertIcon iconType={category} />
        </div>
        <div className="WeatherAlertItem-advisoryText">
          <h2 className="WeatherAlertItem-advisoryTitle">{title}</h2>
          <span className="WeatherAlertItem-advisoryDuration">
            {this._getTimeDuration(startTime, endTime)}
          </span>
        </div>
      </div>
    );
  }
}

/**
 * Class representing the modal for the current weather alerts
 */
class WeatherAlertModal extends Component {
  static propTypes = {
    title: PropTypes.string,
    alerts: PropTypes.array.isRequired,
    expandedAlertIndex: PropTypes.number,
    onAlertClick: PropTypes.func,
    showModal: PropTypes.bool.isRequired,
    onHideModal: PropTypes.func.isRequired,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    titleType: PropTypes.string
  }

  render() {
    const {
      title = '',
      alerts = [],
      expandedAlertIndex = -1,
      onAlertClick,
      showModal,
      onHideModal,
      timestampOptions = {},
      titleType = TITLE_TYPE.MODULE_TITLE
    } = this.props;

    const weatherAlertItems = alerts.map((alert, i) =>
      <WeatherAlertModalItem key={i} index={i} alert={alert} expanded={i === expandedAlertIndex} onClick={onAlertClick} timestampOptions={timestampOptions}/>
    );

    return (
      <Modal className="WeatherAlertModal"
             show={showModal}
             onHide={onHideModal}
             backdrop
             aria-labelledby="contained-modal-title-lg">
        <Modal.Header className="WeatherAlertModal-header" closeButton />
        <Modal.Body>
          { title ? <ComponentTitle titleType={titleType} title={title} classes="WeatherAlertModal-title" /> : null }
          {weatherAlertItems}
        </Modal.Body>
      </Modal>
    );
  }
}

/**
 * Class representing a weather alert item in the modal list
 */
class WeatherAlertModalItem extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    index: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  _onClick() {
    const { onClick, index } = this.props;

    if (onClick) {
      onClick(index);
    }
  }

  render() {
    const {
      alert: {
        'headline': title,
        bulletin,
        'startTime': publishDate,
        backgroundColor = '#d20202'
      } = {},
      expanded = false,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {}
    } = this.props;
    const formattedBulletin = bulletin.toLowerCase();
    const formattedDate = formatWsiDate(publishDate);
    const category = getWeatherCategoryFromCondition(title);

    return (
      <div className="WeatherAlertModalItem">
        <div className="WeatherAlertModalItem-advisory">
          <WeatherAlertIcon iconType={category} backgroundColor={backgroundColor} />
          <div className="WeatherAlertModalItem-header" onClick={() => this._onClick()}>
            <h2 className="WeatherAlertModalItem-advisoryTitle">{title}</h2>
            <Timestamp publishDate={formattedDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
          </div>
          {expanded ? <div className="WeatherAlertModalItem-advisoryText"
                           dangerouslySetInnerHTML={{__html: formattedBulletin.replace(/\n/g, '<br />') }} />
            : null}
        </div>
      </div>
    );
  }
}


export default WeatherAlerts;



// WEBPACK FOOTER //
// ./app/components/weather/AlertList.js